import * as React from "react";
import { Selectors } from "@grandvisionhq/auth";
import { Hooks as Hooks$1 } from "@grandvisionhq/carts-v2";
import { toast } from "@grandvisionhq/common";
import { useLink } from "@grandvisionhq/frk-routing";
import { ReturnsDataLayer } from "@grandvisionhq/fun-returns";
import { Spinner, Alert } from "@grandvisionhq/iris-ui";
import { sha256 } from "@grandvisionhq/lib-utils-w3c";
import { useIntl, useFeatureToggles, useSelector } from "@grandvisionhq/state";
import { Hooks } from "@grandvisionhq/stores-v2";
import { logOrderEvent } from "../../../../data-layer/data-layer/orders.js";
import { useGetOrderDetails } from "../../../hooks/Orders/hooks/use-get-order-details.js";
import OrderDetail from "./OrderDetail.js";
import { clearEvents, makeOrderEvent } from "../../../../data-layer/data-layer/utils.js";
const OrderDetailContainer = ({ orderNumber, shouldShowOrderDate }) => {
  const [hashedEmail, setHashedEmail] = React.useState(null);
  const { getLabel, getFormattedLabel, dateTime, currency } = useIntl();
  const { isPrescriptionGlassesReorderEnabled, isReturnsEnabled, isOrderWorkflowStatusEnabled } = useFeatureToggles();
  const routes = useSelector(Selectors.getFormattedRoutes);
  const prescriptionsUrl = routes?.prescriptions;
  const { data: detail, loading, error } = useGetOrderDetails(orderNumber);
  React.useEffect(() => {
    const hashEmail = async () => {
      if (detail?.customerDetails?.email && isReturnsEnabled) {
        const hashedEmail2 = await sha256(detail.customerDetails.email.toLowerCase());
        setHashedEmail(hashedEmail2);
      }
    };
    hashEmail();
  }, [detail?.customerDetails?.email, isReturnsEnabled]);
  const [storeDetailsQuery, storeDetailsQueryState] = Hooks.useStoreDetailsWithGlobalStoreId();
  const globalStoreId = detail?.shippingAddress?.externalId || void 0;
  const storeName = storeDetailsQueryState.data?.name;
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [isCancelReturnsModalOpen, setCancelReturnsModalOpen] = React.useState(false);
  const [orderDetailsTracked, setOrderDetailsTracked] = React.useState(false);
  const { href: returnUrl } = useLink({
    route: "returns.select-items",
    params: {
      orderNumber: detail?.orderNumber || "",
      email: hashedEmail || ""
    }
  }) || {};
  const onCloseStoreDetailModal = () => {
    setModalOpen(false);
  };
  const onOpenStoreDetailModal = () => {
    setModalOpen(true);
  };
  const onOpenCancelReturnsModal = () => {
    if (!detail) {
      return;
    }
    const { orderNumber: orderNumber2 } = detail;
    logOrderEvent({
      event_type: "my_online_orders",
      event: "return_cancel_order_click",
      order: {
        order_code: orderNumber2
      }
    });
    setCancelReturnsModalOpen(true);
  };
  const onCloseCancelReturnsModal = () => {
    setCancelReturnsModalOpen(false);
  };
  const onClickReturn = () => {
    ReturnsDataLayer.pushReturnsEvent({ interactionType: "return_order" });
  };
  React.useEffect(() => {
    if (globalStoreId) {
      storeDetailsQuery(globalStoreId);
    }
  }, [globalStoreId, storeDetailsQuery]);
  React.useEffect(() => {
    if (!detail || orderDetailsTracked) {
      return;
    }
    const { orderNumber: orderNumber2, orderDate, paymentStatus, shipmentStatus } = detail;
    clearEvents();
    logOrderEvent(
      makeOrderEvent("order details", "view", orderNumber2, orderDate, paymentStatus, shipmentStatus)
    );
    setOrderDetailsTracked(true);
  }, [detail, orderDetailsTracked]);
  const [addToCart, { loading: isReorderLoading }] = Hooks$1.useAddToCart();
  const onReorder = React.useCallback(
    (id) => {
      if (!detail || isReorderLoading) {
        return;
      }
      let lineItems = [];
      if (id) {
        const lineItem = detail.drafts[id];
        if (!lineItem) {
          console.error(`Line item with id "${id}" not found in drafts.`);
          return;
        }
        lineItems = [{ ...lineItem, quantity: 1 }];
      } else {
        lineItems = Object.values(detail.drafts);
      }
      const onError = (errorCodes) => errorCodes.forEach((code) => toast.failed(`cart.${code}`));
      addToCart(lineItems, void 0, onError, (e) => onError([e]));
    },
    [detail, isReorderLoading, addToCart]
  );
  const onTraceOrder = React.useCallback(() => {
    if (!detail) {
      return;
    }
    const { orderNumber: orderNumber2, orderDate, paymentStatus, shipmentStatus } = detail;
    clearEvents();
    logOrderEvent(
      makeOrderEvent(
        "product list",
        "follow order",
        orderNumber2,
        orderDate,
        paymentStatus,
        shipmentStatus
      )
    );
  }, [detail]);
  const onDownloadReceiptClick = () => {
    if (!detail) {
      return;
    }
    const { orderNumber: orderNumber2 } = detail;
    logOrderEvent({
      event_type: "my_online_orders",
      event: "download_receipt_click",
      order: {
        order_code: orderNumber2
      }
    });
  };
  const labels = {
    order: getLabel("common.order"),
    contactDetailsTitle: getLabel("checkout.completed.contactDetails.title"),
    orderDetails: getLabel("account.order.detail"),
    shipmentMethodTitle: getLabel("checkout.shipment.method.title"),
    shipment: getLabel("account.order.detail.shipment"),
    yourOrderDetails: getLabel("account.order.detail.yourOrderItems"),
    itemToBeShipped: getLabel("account.order.detail.itemToBeShipped"),
    itemsToBeShipped: getLabel("account.order.detail.itemsToBeShipped"),
    storeInfo: getLabel("account.orders.storeInfo"),
    shipmentAddress: getLabel("checkout.shipment.address"),
    billingAddress: getLabel("checkout.billingAddress"),
    cancelAndReturns: getLabel("account.cancelAndReturns.label"),
    cancelAndReturnShipment: getLabel("account.cancelAndReturnShipment.label"),
    trackingURL: getLabel("account.order.trackingURL.label"),
    products: getLabel("account.order.detail.products"),
    configuration: getLabel("account.order.detail.configuration"),
    prescription: getLabel("account.order.detail.prescription"),
    error: getFormattedLabel("checkout.errors.general.message", { parseLink: true }),
    makeDeliveryMethodName: (method) => method ? getLabel(`checkout.shipment.method.${method}`) : "-",
    subscriptionDetails: getLabel("cart.item.subscription.title"),
    addOn: getLabel("products.addOn.title"),
    insuranceDetails: getLabel("products.insurance.title"),
    invoiceDetails: getLabel("account.order.detail.invoiceDetails"),
    downloadReceipt: getLabel("common.payment.downloadReceipt"),
    delivery: getLabel("products.deliveryEstimation.label"),
    deliveryTooltip: getLabel("cart.lineItem.deliveryEstimation.tooltip", {
      hideLabelKey: true
    })
  };
  const isPaid = detail?.rawPaymentStatus.toLowerCase() === "paid";
  if (loading) {
    return /* @__PURE__ */ React.createElement(Spinner, { "aria-label": getLabel("common.loadingText") });
  }
  if (error || !detail) {
    return /* @__PURE__ */ React.createElement(Alert, { variant: "error", className: "alert-m-default" }, labels.error);
  }
  return /* @__PURE__ */ React.createElement(
    OrderDetail,
    {
      labels,
      dateTime,
      onCloseStoreDetailModal,
      onOpenStoreDetailModal,
      onTraceOrder,
      onReorder,
      detail,
      lineItems: detail.lineItems,
      shipments: detail.shipments,
      isModalOpen,
      prescriptionsUrl,
      currency,
      storeName,
      isPrescriptionGlassesReorderEnabled,
      isReorderAvailable: detail.isReorderAvailable,
      onOpenCancelReturnsModal,
      onCloseCancelReturnsModal,
      onDownloadReceiptClick,
      returnUrl,
      onClickReturn,
      isCancelReturnsModalOpen,
      isPaid,
      isReturnsEnabled,
      isOrderWorkflowStatusEnabled,
      shouldShowOrderDate
    }
  );
};
export {
  OrderDetailContainer,
  OrderDetailContainer as default
};
//# sourceMappingURL=OrderDetailContainer.js.map
